/**
 * Encrypt/decrypt group passwords using Web Crypto API (AES-GCM).
 * Persisted in sessionStorage (encrypted) so password survives page refresh.
 * Key is stored in sessionStorage to allow decryption after reload.
 */
const STORAGE_KEY = "saboteur_group_pw_key";
const STORAGE_PASSWORDS = "saboteur_group_passwords";
let sessionKey = null;

async function getOrCreateKey() {
  if (sessionKey) return sessionKey;
  const stored = sessionStorage.getItem(STORAGE_KEY);
  if (stored) {
    const raw = Uint8Array.from(atob(stored), (c) => c.charCodeAt(0));
    sessionKey = await crypto.subtle.importKey("raw", raw, { name: "AES-GCM" }, true, ["encrypt", "decrypt"]);
    return sessionKey;
  }
  sessionKey = await crypto.subtle.generateKey(
    { name: "AES-GCM", length: 256 },
    true,
    ["encrypt", "decrypt"]
  );
  const exported = await crypto.subtle.exportKey("raw", sessionKey);
  const arr = new Uint8Array(exported);
  let binary = "";
  for (let i = 0; i < arr.length; i++) binary += String.fromCharCode(arr[i]);
  sessionStorage.setItem(STORAGE_KEY, btoa(binary));
  return sessionKey;
}

async function getIV() {
  return crypto.getRandomValues(new Uint8Array(12));
}

/**
 * Encrypt plaintext. Returns base64 string (iv + ciphertext).
 */
export async function encryptGroupPassword(plaintext) {
  if (!plaintext) return "";
  const key = await getOrCreateKey();
  const iv = await getIV();
  const encoded = new TextEncoder().encode(plaintext);
  const ciphertext = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    key,
    encoded
  );
  const combined = new Uint8Array(iv.length + ciphertext.byteLength);
  combined.set(iv, 0);
  combined.set(new Uint8Array(ciphertext), iv.length);
  let binary = "";
  for (let i = 0; i < combined.length; i++) binary += String.fromCharCode(combined[i]);
  return btoa(binary);
}

/**
 * Decrypt ciphertext (base64 string from encryptGroupPassword).
 */
export async function decryptGroupPassword(ciphertext) {
  if (!ciphertext || typeof ciphertext !== "string") return "";
  try {
    const key = await getOrCreateKey();
    const combined = Uint8Array.from(atob(ciphertext), (c) => c.charCodeAt(0));
    const iv = combined.slice(0, 12);
    const encrypted = combined.slice(12);
    const decrypted = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv },
      key,
      encrypted
    );
    return new TextDecoder().decode(decrypted);
  } catch {
    return "";
  }
}

/** Persist encrypted group passwords (object: groupId -> base64 ciphertext) */
export function persistGroupPasswords(obj) {
  try {
    sessionStorage.setItem(STORAGE_PASSWORDS, JSON.stringify(obj));
  } catch (_) {
    void _; // sessionStorage may be full or unavailable
  }
}

/** Load persisted encrypted group passwords */
export function loadGroupPasswords() {
  try {
    const raw = sessionStorage.getItem(STORAGE_PASSWORDS);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return typeof parsed === "object" && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
}
